import React, { useState, useEffect, useCallback } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import { Foodstuff } from '../../models/Foodstuff'
import { FoodstuffControllerService } from '../../services/FoodstuffControllerService'
import { useTranslation } from 'react-i18next'

interface FoodstuffAutocompleteProps {
    onFoodstuffSelected: (foodstuff: Foodstuff | null) => void
    onResultsFetched?: (foodstuffs: Foodstuff[]) => void
    initialValue?: Foodstuff | null
    label?: string
    placeholder?: string
    debounceDelay?: number
}

export function FoodstuffAutocomplete({
    onFoodstuffSelected,
    onResultsFetched,
    initialValue = null,
    debounceDelay = 300,
  }: FoodstuffAutocompleteProps) {
    
    const [inputValue, setInputValue] = useState('')
    const [options, setOptions] = useState<Foodstuff[]>([])
    const [loading, setLoading] = useState(false)
    const [value, setValue] = useState<Foodstuff | null>(initialValue)

    const fetchFoodstuffs = useCallback(
        (query: string) => {
            if (!query) {
                setOptions([])
                onResultsFetched?.([])
                return
            }

            setLoading(true)
            FoodstuffControllerService.getLikeFoodstuffs(query)
                .then((data) => {
                    setOptions(data)
                    onResultsFetched?.(data)
                })
                .finally(() => setLoading(false))
        },
        [onResultsFetched]
    )

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchFoodstuffs(inputValue)
        }, debounceDelay)

        return () => {
            clearTimeout(handler)
        }
    }, [inputValue, fetchFoodstuffs, debounceDelay])

    const { t } = useTranslation()

    return (
        <Autocomplete
            value={value}
            onChange={(_event, newValue) => {
                setValue(newValue)
                onFoodstuffSelected(newValue)
                setInputValue('')
            }}
            onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue)
            }}
            options={options}
            getOptionLabel={(option) => option.name || ''}
            loading={loading}
            filterOptions={(x) => x}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={t('foodstuff_autocomplete.search_foodstuffs')}
                    placeholder={t('foodstuff_autocomplete.type_to_search')}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    )
}